import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Button,
} from '@vnetwork/ui/primitives';
import { TimeRangePicker } from '@vnetwork/ui/data';
import { X } from 'lucide-react';

// Sentinel for Radix Select "all" option — empty string breaks Radix controlled selects.
const ALL = '__all__';

const ACTION_OPTS = [
  { value: ALL, label: 'All actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'invite_member', label: 'Invite member' },
  { value: 'remove_member', label: 'Remove member' },
  { value: 'update_member', label: 'Update member' },
  { value: 'grant_permission', label: 'Grant permission' },
  { value: 'revoke_permission', label: 'Revoke permission' },
  { value: 'update_permission', label: 'Update permission' },
];

const STATUS_OPTS = [
  { value: ALL, label: 'All statuses' },
  { value: 'success', label: 'Success' },
  { value: 'failure', label: 'Failure' },
];

const RESOURCE_TYPE_OPTS = [
  { value: ALL, label: 'All resource types' },
  { value: 'instance', label: 'Instance' },
  { value: 'kubernetes_cluster', label: 'Kubernetes cluster' },
  { value: 'cdn_domain', label: 'CDN domain' },
  { value: 'team_member', label: 'Team member' },
  { value: 'team_permission', label: 'Team permission' },
];

export interface AuditFilterValues {
  resourceName: string;
  action: string;
  status: string;
  resourceType: string;
  startDate: string;
  endDate: string;
}

export const AUDIT_FILTER_DEFAULTS: AuditFilterValues = {
  resourceName: '',
  action: '',
  status: '',
  resourceType: '',
  startDate: '',
  endDate: '',
};

/** Returns the raw filter value: sentinel maps back to empty string. */
export function fromSentinel(v: string) {
  return v === ALL ? '' : v;
}

const selectStyle: React.CSSProperties = { width: 150 };

// Audit is date-granular; presets map to a start_date N days back from now.
const AUDIT_RANGE_PRESETS = [
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
];
const PRESET_DAYS: Record<string, number> = { '24h': 1, '7d': 7, '30d': 30, '90d': 90 };

function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Toolbar filter controls (selects + date range + clear) for the audit log list.
 * The free-text search Input is rendered separately in the Toolbar `start` slot by
 * ActivityPage so the search stays left-aligned per the app's UX convention.
 * Owns no state — all values/setters come from ActivityPage.
 */
export function AuditLogFilterBar({
  values,
  onChange,
  onClear,
  hasFilters,
}: {
  values: AuditFilterValues;
  onChange: (patch: Partial<AuditFilterValues>) => void;
  onClear: () => void;
  hasFilters: boolean;
}) {
  return (
    <>
      <Select
        value={values.action || ALL}
        onValueChange={(v) => onChange({ action: fromSentinel(v) })}
      >
        <SelectTrigger style={selectStyle}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ACTION_OPTS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={values.status || ALL}
        onValueChange={(v) => onChange({ status: fromSentinel(v) })}
      >
        <SelectTrigger style={selectStyle}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={values.resourceType || ALL}
        onValueChange={(v) => onChange({ resourceType: fromSentinel(v) })}
      >
        <SelectTrigger style={selectStyle}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {RESOURCE_TYPE_OPTS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <TimeRangePicker
        presets={AUDIT_RANGE_PRESETS}
        from={values.startDate ? `${values.startDate} 00:00:00` : undefined}
        to={values.endDate ? `${values.endDate} 23:59:59` : undefined}
        timezoneLabel="GMT+7"
        triggerLabel={
          values.startDate || values.endDate
            ? `${values.startDate || '…'} → ${values.endDate || '…'}`
            : 'Date range'
        }
        onApply={(v) => {
          if (v.from && v.to) {
            onChange({ startDate: v.from.slice(0, 10), endDate: v.to.slice(0, 10) });
          } else if (v.preset && PRESET_DAYS[v.preset]) {
            const now = new Date();
            const from = new Date(now.getTime() - PRESET_DAYS[v.preset] * 86_400_000);
            onChange({ startDate: ymd(from), endDate: ymd(now) });
          } else {
            onChange({ startDate: '', endDate: '' });
          }
        }}
      />

      {hasFilters && (
        <Button variant="ghost" size="sm" startIcon={<X size={14} />} onClick={onClear}>
          Clear
        </Button>
      )}
    </>
  );
}
